/* eslint-disable no-bitwise */
export interface LameGaplessInfo {
  encoderDelay: number; // samples to skip at the start of the decoded buffer
  encoderPadding: number; // samples to trim from the end of the decoded buffer
}

// Parses the LAME/Xing gapless metadata from a raw MP3 ArrayBuffer.
// The Xing header sits in the side-information area of the first MP3 frame.
// Within it, the LAME extension block stores encoder delay and padding as two
// packed 12-bit integers at byte offset 21 of the LAME tag.
// Returns null if the file has no Xing header or no LAME extension.
export function parseLameHeader(buffer: ArrayBuffer): LameGaplessInfo | null {
  const bytes = new Uint8Array(buffer);
  if (bytes.length < 200) return null;

  // Skip ID3v2 tag if present ("ID3" magic at offset 0)
  let offset = 0;
  if (bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) {
    // Size is a 4-byte syncsafe integer at bytes 6-9 (each byte uses only 7 bits)
    const id3Size =
      ((bytes[6] & 0x7f) << 21) |
      ((bytes[7] & 0x7f) << 14) |
      ((bytes[8] & 0x7f) << 7) |
      (bytes[9] & 0x7f);
    offset = 10 + id3Size;
  }

  // Verify MP3 frame sync: first byte 0xFF, upper 3 bits of second byte 0b111
  if (bytes.length < offset + 4) return null;
  if (bytes[offset] !== 0xff || (bytes[offset + 1] & 0xe0) !== 0xe0) return null;

  // MPEG version from bits 4–3 of byte 1: 0b11 = MPEG1, 0b10 = MPEG2, 0b00 = MPEG2.5
  const mpegVersion = (bytes[offset + 1] >> 3) & 0x03;
  // Channel mode from bits 7–6 of byte 3: 0b11 = mono, anything else = stereo
  const channelMode = (bytes[offset + 3] >> 6) & 0x03;

  // Side information size depends on MPEG version and channel count
  let sideInfoSize: number;
  if (mpegVersion === 3) {
    sideInfoSize = channelMode === 3 ? 17 : 32; // MPEG1: mono=17, stereo=32
  } else {
    sideInfoSize = channelMode === 3 ? 9 : 17; // MPEG2/2.5: mono=9, stereo=17
  }

  // Xing/Info header starts immediately after the 4-byte frame header + side info
  const xingOffset = offset + 4 + sideInfoSize;
  if (bytes.length < xingOffset + 8) return null;

  const xingTag = String.fromCharCode(
    bytes[xingOffset],
    bytes[xingOffset + 1],
    bytes[xingOffset + 2],
    bytes[xingOffset + 3],
  );
  if (xingTag !== 'Xing' && xingTag !== 'Info') return null;

  // Flags word indicates which optional fields follow the marker
  const flags =
    (bytes[xingOffset + 4] << 24) |
    (bytes[xingOffset + 5] << 16) |
    (bytes[xingOffset + 6] << 8) |
    bytes[xingOffset + 7];

  // Skip optional Xing fields to land on the LAME extension
  let lameOffset = xingOffset + 8;
  if (flags & 0x01) lameOffset += 4; // total frame count
  if (flags & 0x02) lameOffset += 4; // total byte count
  if (flags & 0x04) lameOffset += 100; // 100-entry TOC
  if (flags & 0x08) lameOffset += 4; // quality indicator

  // LAME extension is 36 bytes; first 4 bytes are the "LAME" identifier
  if (bytes.length < lameOffset + 36) return null;
  const lameTag = String.fromCharCode(
    bytes[lameOffset],
    bytes[lameOffset + 1],
    bytes[lameOffset + 2],
    bytes[lameOffset + 3],
  );
  if (lameTag !== 'LAME') return null;

  // Bytes 21–23 of the LAME tag pack two 12-bit values:
  //   bits 23–12 = encoder delay (samples to skip from start)
  //   bits 11–0  = encoder padding (samples to skip from end)
  // Bytes 19 and 20 are encoding-flags/ATH-type and ABR bitrate — unrelated to gapless.
  const b0 = bytes[lameOffset + 21];
  const b1 = bytes[lameOffset + 22];
  const b2 = bytes[lameOffset + 23];

  return {
    encoderDelay: (b0 << 4) | (b1 >> 4),
    encoderPadding: ((b1 & 0x0f) << 8) | b2,
  };
}
